import React, { useMemo } from 'react';
import { ScrumNote } from '../../types';
import { extractMondayTaskInfo, getMondayItemUrl } from '../../constants/appConstants';
import './ScrumNotesPanel.css';

interface IssueTitleLinkProps {
  title: string;
}

const IssueTitleLink: React.FC<IssueTitleLinkProps> = ({ title }) => {
  const taskInfo = extractMondayTaskInfo(title);
  const mondayUrl = taskInfo ? getMondayItemUrl(taskInfo) : null;

  if (!mondayUrl) {
    return <>{title}</>;
  }

  return (
    <a
      href={mondayUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="monday-link"
      onClick={(e) => e.stopPropagation()}
      title={`Open ${taskInfo!.prefix} task in Monday.com`}
    >
      {title}
      <svg
        className="monday-link-icon"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
};

interface DuplicateNotesModalProps {
  issueTitle: string;
  notes: ScrumNote[];
  onClose: () => void;
  onDelete: (noteId: number) => Promise<void>;
  isAdmin?: boolean;
}

const DuplicateNotesModal: React.FC<DuplicateNotesModalProps> = ({ issueTitle, notes, onClose, onDelete, isAdmin = false }) => {
  const sortedNotes = useMemo(
    () =>
      [...notes].sort(
        (a, b) =>
          new Date(b.occurred_on || b.created_at).getTime() -
          new Date(a.occurred_on || a.created_at).getTime()
      ),
    [notes]
  );

  return (
    <div
      className="scrum-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Scrum history for ${issueTitle}`}
    >
      <div className="scrum-modal">
        <div className="scrum-modal-header">
          <h4><IssueTitleLink title={issueTitle} /> history</h4>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close duplicate history"
          >
            ×
          </button>
        </div>
        <div className="duplicate-note-list">
          {sortedNotes.map(note => (
            <div key={note.id} className="duplicate-note-row">
              <div className="duplicate-note-row-header">
                <span className={`scrum-severity severity-${note.severity}`}>{note.severity}</span>
                {note.is_peer_review ? (
                  <span className="peer-review-badge">Peer Review</span>
                ) : null}
                <span className={`status-chip ${note.status === 'resolved' ? 'resolved' : 'open'}`}>
                  {note.status === 'resolved' ? 'Resolved' : 'Open'}
                </span>
                <span className="duplicate-note-date">
                  {new Date(note.occurred_on || note.created_at).toLocaleDateString()}
                </span>
                {isAdmin && (
                  <button
                    type="button"
                    className="duplicate-note-delete-btn"
                    onClick={() => onDelete(note.id)}
                    aria-label="Delete this entry"
                    title="Delete this entry"
                  >
                    ×
                  </button>
                )}
              </div>
              {note.details && <p className="duplicate-note-details">{note.details}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DuplicateNotesModal;

